var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React from "react";
import useErrors from "../Hooks/useErrors";
import FilterService from "../../Services/Filter.service";
import { getFilterValue, mapFilterConditionForValueEntry, validateFilterCondition, } from "../Presentational/ReportBuilder/ReportBuilderUtils";
const DEFAULT_PAGE_SIZE = 50;
const ReportLoader = (props) => {
    const [filterState, setFilterState] = React.useState({ filter: null });
    // Since the prompts for the report may change before the report is run, and we don't want to
    // run the report every time a prompt is changed, we use a ref.
    const filterConditionsRef = React.useRef([]);
    const [errors, addError, clearErrors] = useErrors();
    const [isRunningFilter, setIsRunningFilter] = React.useState(false);
    const [reportData, setReportData] = React.useState(null);
    const [pageIndex, setPageIndex] = React.useState(0);
    const [hasNextPage, setHasNextPage] = React.useState(false);
    const [runNow, setRunNow] = React.useState(props.runNow);
    const [selectedOrderByColumn, setSelectedOrderByColumn] = React.useState(null);
    const [selectedSortDirection, setSelectedSortDirection] = React.useState(null);
    const [validationErrors, setValidationErrors] = React.useState([]);
    const getEffectiveOrderBy = React.useCallback(() => {
        const { filter } = filterState;
        if (!filter) {
            return {
                orderByColumnId: null,
                // @ts-expect-error - TS2322 - Type '{ orderByColumnId: null; filterSourceRelationshipId: null; sortDirection: null; }' is not assignable to type '{ orderByColumnId: number | null; sortDirection: string | null; }'.
                filterSourceRelationshipId: null,
                sortDirection: null,
            };
        }
        let orderByColumnId = filter.orderByColumn && filter.orderByColumn.id;
        if (selectedOrderByColumn) {
            orderByColumnId = selectedOrderByColumn.id;
        }
        let { sortDirection } = filter;
        if (selectedSortDirection) {
            sortDirection = selectedSortDirection;
        }
        return {
            orderByColumnId,
            sortDirection,
        };
    }, [filterState, selectedOrderByColumn, selectedSortDirection]);
    const getFilterRunRequest = React.useCallback(() => {
        const { filter } = filterState;
        const filterConditions = filterConditionsRef.current;
        if (!filter) {
            throw new Error("Filter not loaded");
        }
        if (!filter.filterSource) {
            throw new Error("No filter source saved on filter");
        }
        // TODO: error handling
        // If the user selected a specific column to order the report by, then use that; otherwise use
        // whatever was saved on the filter as the default ordering.
        const orderBy = getEffectiveOrderBy();
        const pageSize = props.pageSize || filter.pageSize || DEFAULT_PAGE_SIZE;
        const filterRunRequest = {
            id: filter.id,
            name: filter.name,
            filterSourceId: filter.filterSource.id,
            filterConditions: filterConditions.map((fc) => (Object.assign(Object.assign({}, fc), { value: getFilterValue(fc) }))),
            selectedColumns: filter.selectedColumns,
            offset: pageIndex * pageSize,
            pageSize: pageSize + 1,
            orderByColumnId: orderBy.orderByColumnId,
            // @ts-expect-error - TS2322 - Type '{ id: number; name: string; filterSourceId: number; filterConditions: { value: string; filterSourceColumnId: number | null; filterSourceColumn: FilterSourceColumn; ... 8 more ...; relativeOption: string; }[]; ... 8 more ...; maxRows: number; }' is not assignable to type 'FilterRunRequest'.
            sortDirection: orderBy.sortDirection,
            chartType: filter.chartType,
            // @ts-expect-error - TS2339 - Property 'chartSettings' does not exist on type 'Filter'.
            filterChartSettings: filter.chartSettings,
            description: "",
            maxRows: 500,
        };
        return filterRunRequest;
    }, [filterState, getEffectiveOrderBy, pageIndex, props.pageSize]);
    const runFilter = React.useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const filterRunRequest = getFilterRunRequest();
            const currentValidationErrors = filterRunRequest.filterConditions.flatMap((fc) => validateFilterCondition(fc, []));
            setValidationErrors(currentValidationErrors);
            // If there are any validation errors, cut this method short and don't run the report.
            if (currentValidationErrors && currentValidationErrors.length > 0) {
                return;
            }
            setIsRunningFilter(true);
            const result = yield FilterService.runFilter(filterRunRequest);
            // @ts-expect-error - TS2571 - Object is of type 'unknown'.
            setHasNextPage(result.length > filterRunRequest.pageSize - 1);
            // @ts-expect-error - TS2571 - Object is of type 'unknown'.
            setReportData(result.slice(0, filterRunRequest.pageSize - 1));
        }
        catch (e) {
            // @ts-expect-error - TS2349 - This expression is not callable.
            addError("Error running filter", e.message || e.toString());
        }
        finally {
            setIsRunningFilter(false);
        }
    }), [addError, getFilterRunRequest]);
    const runFilterClick = () => {
        // When the component first loads, runNow might be set to 'false' which basically
        // means when the report is loaded, we DO NOT want to run the report right away - we
        // want to wait for the user to press the "Run" button. However, after they press the
        // "Run" button for the first time, we want to put the component into "run mode" which
        // means if anyhthing changes, like page index, sort column, etc, we want to
        // automatically run the report (and this is handled by a useEffect hook).
        if (!runNow) {
            // This triggers a runFilter inherently through the useEffect hook.
            setRunNow(true);
        }
        else {
            // We are already in "run now" mode, so call runFilter manually since nothing
            // else would trigger the report to run.
            runFilter();
        }
    };
    function sort(selectedColumn) {
        // If they clicked the same column again, flip the sort direction.
        if (selectedColumn.id ===
            (selectedOrderByColumn ? selectedOrderByColumn.id : null)) {
            setSelectedSortDirection(selectedSortDirection === "Ascending" ? "Descending" : "Ascending");
        }
        else {
            setSelectedSortDirection("Ascending");
        }
        setSelectedOrderByColumn(selectedColumn);
    }
    function prevPage() {
        setPageIndex(Math.max(pageIndex - 1, 0));
    }
    function nextPage() {
        setPageIndex(pageIndex + 1);
    }
    function setFilterConditionProps(fc, value, displayValue, valueType, relativeOption) {
        const foundCondition = filterConditionsRef.current.find((filterCondition) => filterCondition === fc);
        // Since we are mutating a ref and not a state value here, we don't trigger a re-render
        if (foundCondition) {
            foundCondition.value = value;
            foundCondition.displayValue = displayValue;
            foundCondition.filterConditionValueType = valueType;
            foundCondition.relativeOption = relativeOption;
        }
        // console.log("set filterConditionsRef.current", filterConditionsRef.current);
    }
    React.useEffect(() => {
        // When this component first renders, or if the filter ID changes on the loader, we need
        // to load the filter definition itself.
        const fetchFilter = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const filter = yield FilterService.fetchFilter(props.filterId);
                filterConditionsRef.current = filter.filterConditions.map(mapFilterConditionForValueEntry);
                setFilterState({ filter });
            }
            catch (e) {
                // @ts-expect-error - TS2349 - This expression is not callable.
                addError("Error loading filter", e.toString());
            }
        });
        fetchFilter();
    }, [addError, props.filterId]);
    // When the filter is loaded for the first time, or if the filter state is changed in
    // any way, we need to run the filter.
    // When the component first renders, runNow might be false - it defaults to whatever
    // the caller passes in props for runNow. As soon as the user clicks the run button,
    // the runNow state gets set to true, which triggers this effect. From there on out,
    // this effect is triggered for paging, sorting, etc. and since runNow is set to true
    // the runFilter fn will run when those things change.
    React.useEffect(() => {
        const { filter } = filterState;
        if (runNow && filter) {
            runFilter();
        }
    }, [runNow, filterState, runFilter]);
    return props.render({
        filter: filterState.filter,
        // @ts-expect-error - TS2322 - Type '{ message: string; detail: string; }[] | ((message: string, detail: string) => void)' is not assignable to type '{ message: string; detail: string; }[]'.
        errors,
        // @ts-expect-error - TS2322 - Type '{ message: string; detail: string; }[] | ((message: string, detail: string) => void)' is not assignable to type '() => void'.
        clearErrors,
        runFilter: runFilterClick,
        isRunningFilter,
        reportData,
        pageIndex,
        hasNextPage,
        prevPage,
        nextPage,
        sort,
        orderBy: getEffectiveOrderBy(),
        getFilterRunRequest,
        filterConditions: filterConditionsRef.current,
        setFilterConditionProps,
        validationErrors,
    });
};
export default ReportLoader;
